import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { AgentUsageStats } from "@/api/executive";

function formatCount(value?: number | null): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-US");
}

function formatDuration(ms?: number | null): string {
  if (typeof ms !== "number" || Number.isNaN(ms) || ms <= 0) return "—";
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  const seconds = ms / 1000;
  return `${seconds.toFixed(seconds >= 10 ? 0 : 1)} s`;
}

export type MCAgentUsageCardProps = {
  title: string;
  usage?: AgentUsageStats;
};

export function MCAgentUsageCard({ title, usage }: MCAgentUsageCardProps) {
  const safeUsage = usage ?? {
    last_24h_runs: 0,
    total_runs: 0,
    error_count: 0,
    avg_duration_ms: 0,
  };
  return (
    <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-primary-foreground dark:text-amber-200">{title}</CardTitle>
        <p className="text-xs text-muted-foreground">Automation outputs</p>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-foreground/80">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">24h runs</span>
          <span className="font-semibold text-foreground">
            {formatCount(safeUsage.last_24h_runs ?? (safeUsage as any).last24h)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Total runs</span>
          <span className="font-semibold text-foreground">
            {formatCount(safeUsage.total_runs ?? (safeUsage as any).total)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Errors</span>
          <span className="font-semibold text-rose-300">
            {formatCount(safeUsage.error_count ?? (safeUsage as any).errors)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Avg duration</span>
          <span className="font-semibold text-foreground">
            {formatDuration(safeUsage.avg_duration_ms ?? (safeUsage as any).avg_duration)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default MCAgentUsageCard;
